import '../styles/AddAthlete.css';

import React, { useState } from 'react';

import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import {
  Button,
  Card,
  Container,
  Input,
  Typography,
} from './common';
import PhotoUpload from './common/PhotoUpload';

export default function AddAthlete() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [position, setPosition] = useState('Unknown');
  const [returningstatus, setReturningStatus] = useState('Unknown');
  const [imageUrl, setImageUrl] = useState('');
  const [imageThumbnailUrl, setImageThumbnailUrl] = useState('');
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const db = getFirestore(firebase);

  // Check if athlete number already exists
  const checkAthleteNumberExists = async (athleteNumber: string): Promise<boolean> => {
    if (!sessionId || !athleteNumber.trim()) return false;
    
    try {
      const athletesRef = collection(db, `sessions/${sessionId}/athletes`);
      const q = query(athletesRef, where("number", "==", athleteNumber.trim()));
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking athlete number:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sessionId) {
      setError('Session ID is missing');
      return;
    }
    
    if (!name.trim() || !number.trim()) {
      setError('Please enter both name and number');
      return;
    }

    if (!imageUrl) {
      setError('Please upload an athlete picture');
      return;
    }
    
    // Check for duplicate athlete number
    const isDuplicate = await checkAthleteNumberExists(number);
    if (isDuplicate) {
      setError(`An athlete with number ${number} already exists`);
      return;
    }
    
    try {
      setError('');
      
      // Add athlete to Firestore
      await addDoc(collection(db, `sessions/${sessionId}/athletes`), {
        name,
        number,
        position,
        returningstatus: returningstatus === 'Unknown' ? null : returningstatus,
        imageUrl,
        imageThumbnailUrl,
        createdAt: serverTimestamp(),
        createdBy: "",
      });
      
      // Reset form
      setName('');
      setNumber('');
      setPosition('Unknown');
      setReturningStatus('Unknown');
      setImageUrl('');
      setImageThumbnailUrl('');
      
      // Navigate back to the session page
      navigate(`/session/${sessionId}`);
      
    } catch (error) {
      console.error('Error adding athlete:', error);
      setError('Failed to add athlete');
    }
  };

  if (!sessionId) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">Session ID is missing</div>
          <Button 
            variant="primary" 
            fullWidth 
            onClick={() => navigate('/join')}
          >
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card className='add-athlete-card'>
        <Typography variant="h2">Add Athlete</Typography>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              label="Athlete Name"
              placeholder="Enter athlete name"
              required
            />
          </div>
          
          <div className="form-group">
            <Input
              type="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              label="Athlete Number"
              placeholder="Enter athlete number"
              inputMode="numeric"
              pattern="[0-9]*"
              required
            />
          </div>
          
          <div className="form-group">
            <label className="input-label">Position</label>
            <div className="position-selector">
              {['Unknown', 'Top', 'Base', 'Third', 'Multi'].map((pos) => (
                <button 
                  key={pos}
                  type="button"
                  className={`position-button ${position === pos ? 'selected' : ''}`}
                  onClick={() => setPosition(pos)}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label className="input-label">Status</label>
            <div className="position-selector">
              {['Unknown', 'New', 'Returning', 'Summer'].map((status) => (
                <button 
                  key={status}
                  type="button"
                  className={`position-button ${returningstatus === status ? 'selected' : ''}`}
                  onClick={() => setReturningStatus(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <PhotoUpload
              onImageUpload={({ imageUrl, imageThumbnailUrl }) => {
                setImageUrl(imageUrl);
                setImageThumbnailUrl(imageThumbnailUrl);
              }}
              className="athlete-photo-upload"
              label="Athlete Picture"
            />
          </div>
          
          <div className="form-actions">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => navigate(`/session/${sessionId}`)}
              fullWidth
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={imageUrl == "" && imageThumbnailUrl == ""}
              fullWidth
            >
              {'Add Athlete'}
            </Button>
          </div>
        </form>
      </Card>
    </Container>
  );
} 